import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Container,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  HStack,
  VStack,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  List,
  ListItem,
  ListIcon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  InputGroup,
  InputLeftAddon,
  Select,
  Input,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useAuth } from "../contexts/AuthContext";
import {
  FiCheck,
  FiMic,
  FiMessageSquare,
  FiMail,
  FiZap,
  FiUsers,
  FiCalendar,
} from "react-icons/fi";

// Common IANA timezones offered in the per-company timezone selector.
const TIMEZONES: Array<{ value: string; label: string }> = [
  { value: "America/New_York", label: "US Eastern (New York)" },
  { value: "America/Chicago", label: "US Central (Chicago)" },
  { value: "America/Denver", label: "US Mountain (Denver)" },
  { value: "America/Phoenix", label: "US Arizona (Phoenix)" },
  { value: "America/Los_Angeles", label: "US Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "US Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "US Hawaii (Honolulu)" },
  { value: "America/Toronto", label: "Canada Eastern (Toronto)" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "America/Sao_Paulo", label: "Brazil (São Paulo)" },
  { value: "Europe/London", label: "UK (London)" },
  { value: "Europe/Madrid", label: "Spain (Madrid)" },
  { value: "Europe/Paris", label: "Central Europe (Paris)" },
  { value: "Asia/Dubai", label: "UAE (Dubai)" },
  { value: "Asia/Kolkata", label: "India (Kolkata)" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Japan (Tokyo)" },
  { value: "Australia/Sydney", label: "Australia (Sydney)" },
  { value: "UTC", label: "UTC" },
];

// ─── Plan configuration ────────────────────────────────────────────────────────
// Price IDs are injected from env at build time via REACT_APP_ prefix.
// Guillermo: set these after creating the Stripe products.
const PLANS: { priceId: string; name: string; quarterlyPrice: number; agentLimit: number; features: string[] }[] = [
  {
    priceId: process.env.REACT_APP_STRIPE_STARTER_QUARTERLY_PRICE_ID || "",
    name: "Starter",
    quarterlyPrice: 3000,
    agentLimit: 2,
    features: ["2 total agent slots", "Voice, SMS, Email, WhatsApp", "$0.30/min voice", "$0.02/msg SMS & WhatsApp", "$0.02/email"],
  },
  {
    priceId: process.env.REACT_APP_STRIPE_GROWTH_QUARTERLY_PRICE_ID || "",
    name: "Growth",
    quarterlyPrice: 4500,
    agentLimit: 4,
    features: ["4 total agent slots", "Voice, SMS, Email, WhatsApp", "$0.30/min voice", "$0.02/msg SMS & WhatsApp", "$0.02/email"],
  },
  {
    priceId: process.env.REACT_APP_STRIPE_SCALE_QUARTERLY_PRICE_ID || "",
    name: "Scale",
    quarterlyPrice: 6000,
    agentLimit: 8,
    features: ["8 total agent slots", "Voice, SMS, Email, WhatsApp", "$0.30/min voice", "$0.02/msg SMS & WhatsApp", "$0.02/email"],
  },
];

// ─── Types ─────────────────────────────────────────────────────────────────────
interface Subscription {
  company_name: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  tier: string;
  agent_limit: number;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

interface UsageChannel {
  quantity: number;
  cost: number;
}

interface UsageSummary {
  period: string;
  voice: UsageChannel;
  sms: UsageChannel;
  email: UsageChannel;
  whatsapp: UsageChannel;
  totalCost: number;
  threshold: number;
  remainingToThreshold: number;
}

interface Invoice {
  id: number;
  stripe_invoice_id: string;
  amount_paid: number;
  status: string;
  hosted_invoice_url: string;
  invoice_pdf: string;
  receipt_url?: string;
  paid_at: string;
  created_at: string;
}

// ─── Helper ────────────────────────────────────────────────────────────────────
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function capitalize(s: string | undefined): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Component ─────────────────────────────────────────────────────────────────
const BillingPage: React.FC = () => {
  const { user, isAdmin, currentClientId, clients, billingMode, refreshCredits } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [agentCount, setAgentCount] = useState<number | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [usageRange, setUsageRange] = useState<"today" | "week" | "month" | "3months" | "custom">("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [tzSaving, setTzSaving] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Array<{id: string, amount: number, transaction_type: string, description: string, stripe_payment_intent_id: string | null, created_at: string}>>([]);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [topupAmount, setTopupAmount] = useState<number>(25);
  const [topupLoading, setTopupLoading] = useState(false);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(false);
  const [autoRechargeThreshold, setAutoRechargeThreshold] = useState(5);
  const [autoRechargeAmount, setAutoRechargeAmount] = useState(50);
  const [autoRechargeSaving, setAutoRechargeSaving] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [saveCardLoading, setSaveCardLoading] = useState(false);
  const [cardDetails, setCardDetails] = useState<{brand: string, last4: string, exp_month: number, exp_year: number} | null>(null);
  const [localCreditsData, setLocalCreditsData] = useState<{total_credits: number, used_credits: number} | null>(null);
  const topupSectionRef = useRef<HTMLDivElement>(null);

  const getHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "user-id": user?.id || "",
    };
    if (isAdmin) {
      headers["x-is-admin"] = "true";
      if (currentClientId) {
        const selectedClient = clients.find((c: any) => c.id === currentClientId);
        if (selectedClient?.company_name) {
          headers["x-company-name"] = selectedClient.company_name;
        }
      }
    }
    return headers;
  }, [user, isAdmin, currentClientId, clients]);

  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getHeaders();

      const [subRes, invoicesRes, txRes] = await Promise.all([
        fetch("/api/billing/subscription", { headers }),
        fetch("/api/billing/invoices", { headers }),
        fetch("/api/billing/transactions", { headers }),
      ]);

      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data.subscription || null);
        setAgentCount(typeof data.agentCount === "number" ? data.agentCount : null);
        if (data.credits?.auto_recharge_enabled !== undefined) {
          setAutoRechargeEnabled(data.credits.auto_recharge_enabled || false);
          setAutoRechargeThreshold(data.credits.auto_recharge_threshold || 5);
          setAutoRechargeAmount(data.credits.auto_recharge_amount || 50);
          setHasPaymentMethod(!!data.credits.stripe_payment_method_id);
          setLocalCreditsData({ total_credits: data.credits.total_credits || 0, used_credits: data.credits.used_credits || 0 });
        }
        // Fetch card details if a payment method is saved
        if (data.credits?.stripe_payment_method_id) {
          try {
            const pmRes = await fetch('/api/billing/payment-method', { headers });
            if (pmRes.ok) {
              const pmData = await pmRes.json();
              setCardDetails(pmData.card || null);
            }
          } catch {
            setCardDetails(null);
          }
        } else {
          setCardDetails(null);
        }
      }
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }
      if (txRes.ok) {
        const data = await txRes.json();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error("Failed to load billing data:", err);
      toast({ title: 'Failed to load billing data', description: 'Please refresh the page.', status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (!user) return;
    setSubscription(null);
    setUsage(null);
    setInvoices([]);
    setTransactions([]);
    setLocalCreditsData(null);
    fetchBillingData();
  }, [currentClientId, user, fetchBillingData]);

  // Usage is fetched separately so the date-range filter can refetch on change.
  const fetchUsage = useCallback(async () => {
    try {
      const DAY = 86400000;
      const now = new Date();
      let startIso: string;
      if (usageRange === "today") {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        startIso = d.toISOString();
      } else if (usageRange === "week") {
        startIso = new Date(now.getTime() - 7 * DAY).toISOString();
      } else if (usageRange === "3months") {
        startIso = new Date(now.getTime() - 90 * DAY).toISOString();
      } else if (usageRange === "custom") {
        startIso = customFrom ? new Date(customFrom).toISOString() : new Date(now.getTime() - 30 * DAY).toISOString();
      } else {
        startIso = new Date(now.getTime() - 30 * DAY).toISOString(); // month
      }
      const endIso =
        usageRange === "custom" && customTo
          ? new Date(customTo + "T23:59:59").toISOString()
          : now.toISOString();

      const res = await fetch(
        `/api/billing/usage?from=${encodeURIComponent(startIso)}&to=${encodeURIComponent(endIso)}`,
        { headers: getHeaders() }
      );
      if (res.ok) setUsage(await res.json());
    } catch {
      /* ignore — usage card simply won't update */
    }
  }, [usageRange, customFrom, customTo, getHeaders]);

  useEffect(() => {
    if (user) fetchUsage();
  }, [fetchUsage, user]);

  // Per-company timezone (used to group Daily Usage by the client's local day).
  const fetchTimezone = useCallback(async () => {
    try {
      const res = await fetch("/api/company-timezone", { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data?.timezone) setTimezone(data.timezone);
      }
    } catch {
      /* ignore */
    }
  }, [getHeaders]);

  useEffect(() => {
    if (user) fetchTimezone();
  }, [fetchTimezone, user]);

  const saveTimezone = async (tz: string) => {
    setTimezone(tz);
    setTzSaving(true);
    try {
      const res = await fetch("/api/company-timezone", {
        method: "PUT",
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: tz }),
      });
      if (res.ok) {
        toast({ title: "Timezone updated", description: "Daily usage now groups by this timezone.", status: "success", duration: 4000, isClosable: true });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Could not update timezone", description: err.error || "", status: "error", duration: 5000, isClosable: true });
      }
    } catch (e: any) {
      toast({ title: "Could not update timezone", description: e?.message || "", status: "error", duration: 5000, isClosable: true });
    } finally {
      setTzSaving(false);
    }
  };

  // Check for Stripe redirect success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success")) {
      toast({ title: "Subscription activated!", status: "success", duration: 5000, isClosable: true });
      window.history.replaceState({}, "", "/billing");
      setTimeout(fetchBillingData, 2000);
    }
    if (params.get("canceled")) {
      toast({ title: "Checkout canceled", status: "info", duration: 3000, isClosable: true });
      window.history.replaceState({}, "", "/billing");
    }
    if (params.get("topup") === "success") {
      const amt = params.get("amount");
      toast({ title: `Balance topped up!${amt ? ` +$${amt}` : ""}`, status: "success", duration: 5000, isClosable: true });
      window.history.replaceState({}, "", "/billing");
      refreshCredits();
      setTimeout(() => fetchBillingData(), 2000);
    }
    if (params.get("topup") === "canceled") {
      toast({ title: "Top-up canceled", status: "info", duration: 3000, isClosable: true });
      window.history.replaceState({}, "", "/billing");
    }
    if (params.get("setup") === "success") {
      toast({ title: "Card saved!", description: "Your payment method has been saved for auto-recharge.", status: "success", duration: 5000, isClosable: true });
      window.history.replaceState({}, "", "/billing");
      setTimeout(fetchBillingData, 2000);
    }
    if (params.get("setup") === "canceled") {
      toast({ title: "Card setup cancelled", status: "info", duration: 3000 });
      window.history.replaceState({}, "", "/billing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (priceId: string) => {
    if (!priceId) {
      toast({ title: "Plan not configured yet", description: "Contact support to subscribe.", status: "warning", duration: 4000, isClosable: true });
      return;
    }
    try {
      setCheckingOut(priceId);
      const res = await fetch("/api/billing/create-subscription-checkout", {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.error || "Checkout failed");
    } catch (err: any) {
      toast({ title: "Checkout failed", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setCheckingOut(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? It will remain active until the end of the current quarter.")) return;
    try {
      setCancelLoading(true);
      const res = await fetch("/api/billing/cancel-subscription", {
        method: "POST",
        headers: getHeaders(),
      });
      if (res.ok) {
        toast({ title: "Subscription will cancel at period end", status: "info", duration: 5000, isClosable: true });
        fetchBillingData();
      }
    } catch (err: any) {
      toast({ title: "Cancel failed", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleTopup = async () => {
    if (topupAmount < 25) {
      toast({ title: "Minimum top-up is $25", status: "error", duration: 3000 });
      return;
    }
    setTopupLoading(true);
    try {
      const res = await fetch("/api/billing/topup", {
        method: "POST",
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ amount: topupAmount }),
      });
      const data = await res.json();
      if (data.method === 'saved_card') {
        toast({ title: `Balance topped up!`, description: `+$${topupAmount} added via saved card.`, status: "success", duration: 5000, isClosable: true });
        refreshCredits();
        fetchBillingData();
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: data.error || 'Top-up failed', description: 'Your card may have been declined. Please update your card and try again.', status: 'error', duration: 6000 });
      }
    } catch {
      toast({ title: "Top-up failed", status: "error", duration: 3000 });
    } finally {
      setTopupLoading(false);
    }
  };

  const handleSaveCard = async () => {
    setSaveCardLoading(true);
    try {
      const res = await fetch('/api/billing/create-setup-checkout', {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: 'Error', description: data.error || 'Could not redirect to card setup', status: 'error', duration: 4000 });
      }
    } catch {
      toast({ title: 'Error', description: 'Card setup failed', status: 'error', duration: 4000 });
    } finally {
      setSaveCardLoading(false);
    }
  };

  const handleSaveAutoRecharge = async () => {
    setAutoRechargeSaving(true);
    try {
      const res = await fetch('/api/billing/auto-recharge-settings', {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: autoRechargeEnabled,
          threshold: autoRechargeThreshold,
          amount: autoRechargeAmount,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Auto-recharge settings saved', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Failed to save', status: 'error', duration: 3000 });
      }
    } catch {
      toast({ title: 'Failed to save settings', status: 'error', duration: 3000 });
    } finally {
      setAutoRechargeSaving(false);
    }
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  const currentPlan = PLANS.find(p => subscription?.stripe_price_id === p.priceId || subscription?.tier === p.name.toLowerCase());
  const isActive = subscription?.status === "active";
  const agentLimit = subscription?.agent_limit || currentPlan?.agentLimit || 0;

  // ─── Credit status computed from localCreditsData (single source of truth) ──
  const available_credits = localCreditsData !== null
    ? localCreditsData.total_credits - localCreditsData.used_credits
    : null;
  const total_credits = localCreditsData?.total_credits ?? null;
  const isCreditsEmpty = available_credits !== null && available_credits <= 0;
  // Low if < 20% of total (but at least $20 threshold)
  const lowThreshold = total_credits !== null ? Math.max(total_credits * 0.2, 20) : 20;
  const isCreditsLow = !isCreditsEmpty && available_credits !== null && available_credits < lowThreshold;

  const scrollToTopup = () => {
    topupSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container maxW="container.xl" py={8}>
      <Heading size="lg" mb={6} id="topup-section">Billing</Heading>

      {/* ── Credit alerts ── */}
      {localCreditsData && (() => {
        const available = localCreditsData.total_credits - localCreditsData.used_credits;
        const pct = localCreditsData.total_credits > 0 ? available / localCreditsData.total_credits : 0;
        if (available <= 0) return (
          <Alert status="error" borderRadius="md" mb={6} flexDirection="column" alignItems="flex-start" gap={2}>
            <Flex align="center" gap={2}><AlertIcon /><AlertTitle>Account Suspended — No Credits Available</AlertTitle></Flex>
            <AlertDescription>
              {billingMode === 'credits'
                ? 'Your account has run out of credits. Please contact your administrator to add credits.'
                : 'Your account has run out of credits. All agents are paused. Add credits below to resume service.'}
            </AlertDescription>
            {billingMode !== 'credits' && (
              <Button size="sm" colorScheme="red" mt={1} onClick={() => topupSectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}>
                Add Credits Now
              </Button>
            )}
          </Alert>
        );
        if (pct < 0.2 || available < 20) return (
          <Alert status="warning" borderRadius="md" mb={6}>
            <AlertIcon />
            <AlertTitle>Running Low</AlertTitle>
            <AlertDescription>
              {billingMode === 'credits'
                ? `Only ${available.toFixed(0)} credits remaining. Contact your administrator to top up.`
                : `Only $${available.toFixed(2)} remaining. Consider topping up soon.`
              }
            </AlertDescription>
          </Alert>
        );
        return null;
      })()}



      {/* ── Balance / Top-Up — shown for pay-as-you-go (dollars) clients ────── */}
      {billingMode !== 'credits' && (
        <>
        <Box bg="white" border="1px" borderColor="gray.200" borderRadius="xl" p={6} mb={6} ref={topupSectionRef}>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text fontWeight="bold" fontSize="lg">Usage Balance</Text>
              <Text fontSize="sm" color="gray.500">Pay-as-you-go · $0.30/min voice · $0.02/SMS · $0.02/email</Text>
            </Box>
            <HStack spacing={6}>
              <Box textAlign="center">
                <Text fontSize="xs" color="gray.500" mb={1}>Total Budget</Text>
                <Text fontSize="xl" fontWeight="bold" color="gray.700">${localCreditsData ? localCreditsData.total_credits.toFixed(2) : "—"}</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="xs" color="gray.500" mb={1}>Used</Text>
                <Text fontSize="xl" fontWeight="bold" color="orange.500">${localCreditsData ? localCreditsData.used_credits.toFixed(2) : "—"}</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="xs" color="gray.500" mb={1}>Remaining</Text>
                <Text fontSize="xl" fontWeight="bold" color={localCreditsData !== null && (localCreditsData.total_credits - localCreditsData.used_credits) < 0 ? "red.500" : "green.500"}>${localCreditsData !== null ? (localCreditsData.total_credits - localCreditsData.used_credits).toFixed(2) : "—"}</Text>
              </Box>
            </HStack>
          </Flex>
          <Divider mb={4} />
          <Text fontSize="sm" fontWeight="medium" mb={3}>Add Balance</Text>
          <HStack>
            <InputGroup w="160px">
              <InputLeftAddon>$</InputLeftAddon>
              <NumberInput min={25} max={10000} value={topupAmount} onChange={(_, v) => setTopupAmount(isNaN(v) ? 25 : v)} w="full">
                <NumberInputField borderLeftRadius={0} />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </InputGroup>
            <Button colorScheme="green" onClick={handleTopup} isLoading={topupLoading} loadingText="Redirecting...">
              Add ${topupAmount}
            </Button>
          </HStack>
          <Text fontSize="xs" color="gray.400" mt={2}>Minimum $25 · Processed via Stripe · Separate from subscription</Text>
        </Box>

        {/* Auto-Recharge Settings */}
        {isAdmin && (
        <Box bg="white" border="1px" borderColor="gray.200" borderRadius="xl" p={6} mb={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text fontWeight="bold" fontSize="lg">Auto-Recharge</Text>
              <Text fontSize="sm" color="gray.500">Automatically top up when balance runs low</Text>
            </Box>
            <Switch
              isChecked={autoRechargeEnabled}
              onChange={(e) => setAutoRechargeEnabled(e.target.checked)}
              colorScheme="green"
              size="lg"
            />
          </Flex>
          {autoRechargeEnabled && (
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Recharge when balance drops below</Text>
                  <InputGroup>
                    <InputLeftAddon>$</InputLeftAddon>
                    <NumberInput min={0} max={100} value={autoRechargeThreshold} onChange={(_, v) => setAutoRechargeThreshold(isNaN(v) ? 5 : v)} w="full">
                      <NumberInputField borderLeftRadius={0} />
                      <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                    </NumberInput>
                  </InputGroup>
                </Box>
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Recharge amount</Text>
                  <InputGroup>
                    <InputLeftAddon>$</InputLeftAddon>
                    <NumberInput min={25} max={1000} value={autoRechargeAmount} onChange={(_, v) => setAutoRechargeAmount(isNaN(v) ? 50 : v)} w="full">
                      <NumberInputField borderLeftRadius={0} />
                      <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                    </NumberInput>
                  </InputGroup>
                </Box>
              </HStack>
              {!hasPaymentMethod ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box flex={1}>
                    <AlertDescription fontSize="sm" mb={2}>
                      No payment method saved. Add a card to enable auto-recharge.
                    </AlertDescription>
                    <Button size="sm" colorScheme="blue" mt={2} onClick={handleSaveCard} isLoading={saveCardLoading} loadingText="Redirecting...">
                      Add Card
                    </Button>
                  </Box>
                </Alert>
              ) : (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box flex={1}>
                    {cardDetails ? (
                      <AlertDescription fontSize="sm">
                        <HStack justify="space-between">
                          <Text>
                            {capitalize(cardDetails.brand)} •••• {cardDetails.last4} — expires {String(cardDetails.exp_month).padStart(2,'0')}/{String(cardDetails.exp_year).slice(-2)}
                          </Text>
                          <Button size="xs" colorScheme="blue" variant="outline" onClick={handleSaveCard} isLoading={saveCardLoading} loadingText="Redirecting...">
                            Update Card
                          </Button>
                        </HStack>
                      </AlertDescription>
                    ) : (
                      <AlertDescription fontSize="sm">
                        <HStack justify="space-between">
                          <Text>Payment method saved ✓ Auto-recharge is ready.</Text>
                          <Button size="xs" colorScheme="blue" variant="outline" onClick={handleSaveCard} isLoading={saveCardLoading} loadingText="Redirecting...">
                            Update Card
                          </Button>
                        </HStack>
                      </AlertDescription>
                    )}
                  </Box>
                </Alert>
              )}
            </VStack>
          )}
          <Button mt={4} colorScheme="blue" size="sm" onClick={handleSaveAutoRecharge} isLoading={autoRechargeSaving}>
            Save Settings
          </Button>
        </Box>
        )}
        </>
      )}

      {/* ── Cancellation notice ─────────────────────────────────────────────── */}
      {subscription?.cancel_at_period_end && (
        <Alert status="warning" mb={6} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Subscription ending</AlertTitle>
          <AlertDescription>
            Your subscription will cancel on {formatDate(subscription.current_period_end)}. You will lose access to your agents after that date.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Current Plan + Agent Slots ───────────────────────────────────────── */}
      {isActive && currentPlan && (
        <Card mb={6}>
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Current Plan</Heading>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>Active</Badge>
            </Flex>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Plan</Text>
                <Text fontWeight="bold" fontSize="xl">{currentPlan.name}</Text>
                <Text color="gray.500">${(currentPlan.quarterlyPrice / 3).toLocaleString()}/mo · billed quarterly</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Agent Slots</Text>
                <HStack>
                  <Icon as={FiUsers} />
                  <Text fontWeight="bold" fontSize="xl">
                    {agentCount !== null ? agentCount : "—"} of {agentLimit}
                  </Text>
                </HStack>
                <Text color="gray.500" fontSize="sm">agents used across all channels</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Current Period</Text>
                <HStack>
                  <Icon as={FiCalendar} />
                  <Text>{formatDate(subscription?.current_period_start)} – {formatDate(subscription?.current_period_end)}</Text>
                </HStack>
              </Box>
            </SimpleGrid>

            <Divider my={4} />

            <HStack spacing={3}>
              {subscription?.stripe_subscription_id && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={handleCancel}
                  isLoading={cancelLoading}
                  isDisabled={subscription?.cancel_at_period_end}
                >
                  Cancel Subscription
                </Button>
              )}
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* ── Timezone (per-company) ───────────────────────────────────────────── */}
      <Card mb={6}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <Text fontWeight="bold" fontSize="lg">Timezone</Text>
              <Text fontSize="sm" color="gray.500">
                Daily usage is grouped by this client's local day. Set it to the client's timezone.
              </Text>
            </Box>
            <Select
              maxW="320px"
              value={timezone}
              isDisabled={tzSaving}
              onChange={(e) => saveTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
              {!TIMEZONES.some((t) => t.value === timezone) && (
                <option value={timezone}>{timezone}</option>
              )}
            </Select>
          </Flex>
        </CardBody>
      </Card>

      {/* ── Usage This Period ────────────────────────────────────────────────── */}
      {isActive && usage && (
        <Card mb={6}>
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Heading size="md">Usage</Heading>
              <HStack spacing={2} wrap="wrap">
                <Select
                  size="sm"
                  width="auto"
                  value={usageRange}
                  onChange={(e) => setUsageRange(e.target.value as typeof usageRange)}
                >
                  <option value="today">Today</option>
                  <option value="week">Last 7 days</option>
                  <option value="month">Last 30 days</option>
                  <option value="3months">Last 3 months</option>
                  <option value="custom">Custom range</option>
                </Select>
                {usageRange === "custom" && (
                  <>
                    <Input
                      size="sm"
                      type="date"
                      width="auto"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                    />
                    <Text fontSize="sm" color="gray.500">–</Text>
                    <Input
                      size="sm"
                      type="date"
                      width="auto"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </>
                )}
                <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                  {usage.period}
                </Badge>
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
              {/* Voice */}
              <Stat>
                <StatLabel>
                  <HStack spacing={1}><Icon as={FiMic} /><Text>Voice</Text></HStack>
                </StatLabel>
                <StatNumber>${usage.voice.cost.toFixed(2)}</StatNumber>
                <StatHelpText>{usage.voice.quantity.toFixed(1)} minutes</StatHelpText>
              </Stat>
              {/* SMS */}
              <Stat>
                <StatLabel>
                  <HStack spacing={1}><Icon as={FiMessageSquare} /><Text>SMS</Text></HStack>
                </StatLabel>
                <StatNumber>${usage.sms.cost.toFixed(2)}</StatNumber>
                <StatHelpText>{usage.sms.quantity.toFixed(0)} messages</StatHelpText>
              </Stat>
              {/* Email */}
              <Stat>
                <StatLabel>
                  <HStack spacing={1}><Icon as={FiMail} /><Text>Email</Text></HStack>
                </StatLabel>
                <StatNumber>${usage.email.cost.toFixed(2)}</StatNumber>
                <StatHelpText>{usage.email.quantity.toFixed(0)} emails</StatHelpText>
              </Stat>
              {/* WhatsApp */}
              <Stat>
                <StatLabel>
                  <HStack spacing={1}><Icon as={FiZap} /><Text>WhatsApp</Text></HStack>
                </StatLabel>
                <StatNumber>${usage.whatsapp.cost.toFixed(2)}</StatNumber>
                <StatHelpText>{usage.whatsapp.quantity.toFixed(0)} messages</StatHelpText>
              </Stat>
            </SimpleGrid>

            <Divider mb={4} />

            {/* Usage Total */}
            <Box>
              <Text fontWeight="medium">Usage total: <b>${usage.totalCost.toFixed(2)}</b></Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                Usage is deducted from your pre-loaded balance. Add balance anytime to continue using your agents.
              </Text>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* ── Plan Selection (shown when no active subscription) ───────────────── */}
      {!isActive && (
        <Box mb={6}>
          <Heading size="md" mb={4}>Choose a Plan</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {PLANS.map((plan) => (
              <Card key={plan.name} border="2px solid" borderColor={plan.name === "Growth" ? "purple.400" : "gray.200"}>
                <CardBody>
                  <VStack align="start" spacing={3}>
                    {plan.name === "Growth" && (
                      <Badge colorScheme="purple">Most Popular</Badge>
                    )}
                    <Heading size="md">{plan.name}</Heading>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold">
                        ${(plan.quarterlyPrice / 3).toLocaleString()}<Text as="span" fontSize="sm" fontWeight="normal">/mo</Text>
                      </Text>
                      <Text fontSize="sm" color="gray.500">billed ${plan.quarterlyPrice.toLocaleString()}/quarter</Text>
                    </Box>
                    <Divider />
                    <List spacing={2} w="full">
                      {plan.features.map((f) => (
                        <ListItem key={f}>
                          <HStack>
                            <ListIcon as={FiCheck} color="green.500" />
                            <Text fontSize="sm">{f}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      colorScheme="purple"
                      w="full"
                      onClick={() => handleSubscribe(plan.priceId)}
                      isLoading={checkingOut === plan.priceId}
                      isDisabled={!!checkingOut}
                    >
                      Get Started
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
          <Text fontSize="sm" color="gray.500" mt={3}>
            <Icon as={FiZap} mr={1} />
            All plans include $0.30/min voice · $0.02/msg SMS & WhatsApp · $0.02/email.
            Usage is billed from your pre-loaded balance.
          </Text>
        </Box>
      )}

      {/* ── Upgrade options (shown when subscribed, to prompt upgrades) ─────── */}
      {isActive && currentPlan && (
        <Card mb={6}>
          <CardHeader pb={2}>
            <Heading size="md">Upgrade Plan</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {PLANS.filter(p => p.name !== currentPlan.name).map(plan => (
                <Box key={plan.name} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                  <Text fontWeight="bold" mb={1}>{plan.name}</Text>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    {plan.agentLimit} agents · ${(plan.quarterlyPrice / 3).toLocaleString()}/mo
                  </Text>
                  <Button size="sm" variant="outline" colorScheme="purple"
                    onClick={() => handleSubscribe(plan.priceId)}
                    isLoading={checkingOut === plan.priceId}
                  >
                    Switch to {plan.name}
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* ── Invoices ────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader pb={2}>
          <Heading size="md">Invoices</Heading>
        </CardHeader>
        <CardBody>
          {invoices.length === 0 ? (
            <Text color="gray.500">No invoices yet.</Text>
          ) : (
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Status</Th>
                    <Th>Receipt</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invoices.map((inv) => (
                    <Tr key={inv.stripe_invoice_id}>
                      <Td>{formatDate(inv.paid_at || inv.created_at)}</Td>
                      <Td isNumeric>${inv.amount_paid.toFixed(2)}</Td>
                      <Td>
                        <Badge colorScheme={inv.status === "paid" ? "green" : inv.status === "payment_failed" ? "red" : "yellow"}>
                          {capitalize(inv.status?.replace("_", " "))}
                        </Badge>
                      </Td>
                      <Td>
                        {inv.hosted_invoice_url && (
                          <Button as="a" href={inv.hosted_invoice_url} target="_blank" size="xs" variant="link" rightIcon={<ExternalLinkIcon />}>
                            View
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>
      {/* ── Recharge / Top-Up History ─────────────────────────────────────── */}
      {billingMode !== "credits" && transactions.length > 0 && (
        <Box bg="white" border="1px" borderColor="gray.200" borderRadius="xl" p={6} mb={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text fontWeight="bold" fontSize="lg">Balance History</Text>
              <Text fontSize="sm" color="gray.500">All top-ups and subscription credits</Text>
            </Box>
          </Flex>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th isNumeric>Amount</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((tx) => (
                <Tr key={tx.id}>
                  <Td fontSize="sm" color="gray.600">
                    {new Date(tx.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </Td>
                  <Td>
                    <Badge colorScheme={tx.transaction_type === "purchase" ? "green" : "blue"} variant="subtle">
                      {tx.transaction_type === "purchase" ? "Top-up" : "Subscription"}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.700">{tx.description}</Td>
                  <Td isNumeric fontWeight="semibold" color="green.600">+${Number(tx.amount).toFixed(2)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Container>
  );
};

export default BillingPage;
